import { NavigateFunction } from 'react-router-dom';
import { AppDispatch } from '@/store';
import { flwOrchestratorApi } from '@/services';
import type { PolicyEvaluateResponse, Environment } from '@/models/flw-orchestrator.types';
import { Device, DevicePlatform, PolicyEvaluationStep } from '@/models/flw-orchestrator.types';
import { ROUTES } from '@/constants/routes';
import { NavigationService } from './navigationService';
import { PolicyCacheService } from './policyCacheService';
import { retryOnProvisioning } from '@/utils/retry';
import {
  collectAuthStatus,
  collectCompanyPortalStatus,
  collectMfaPolicyResult,
  collectMamPolicyResult,
  trackError,
  recordRecoverableError,
  startPolicyEvaluationTiming,
  endPolicyEvaluationTiming,
} from '@/telemetry/telemetryEvents';
import { telemetryManager } from '@/telemetry/telemetryManager';
import { logDev as debugLog, errorDev } from '@/logging/devConsole';
import { getFeatureFlag } from '@/services/ecs';

/**
 * Policy evaluation parameters required for both MFA and MAM evaluation
 */
export interface PolicyEvaluationParams {
  env: Environment;
  userId: string;
  applicationId: string;
  devicePlatform: DevicePlatform;
  clientAppType: 'browser' | 'publicClient' | 'mobileAppsAndDesktopClients';
  region: string;
  selectedDevice: Device;
}

/**
 * Result of a complete policy evaluation flow (both MFA and MAM)
 */
export interface PolicyEvaluationResult {
  mfaResult: PolicyEvaluateResponse;
  mamResult?: PolicyEvaluateResponse;
  navigated: boolean;
  error?: unknown; 
  failedStep?: PolicyEvaluationStep;
}

function handleRetryFailure(step: PolicyEvaluationStep, attempt: number, error: unknown, durationMs?: number): void {
  const reason = step === PolicyEvaluationStep.MFA ? 'mfa api failure' : 'mam api failure';
  // For attempt 0 (initial call), use 'mfa-call' or 'mam-call'
  // For attempts 1+ (actual retries), use the retry step from PolicyEvaluationStep
  const effectiveStep = attempt === 0 ? step : step;
  recordRecoverableError(reason, effectiveStep, attempt, error, durationMs);
}

/**
 * PolicyEvaluationService - Consolidated service for all MFA/MAM evaluation logic
 * This consolidates evaluation logic from:
 * - AuthRedirectHandler (initial auth flow)
 * - AuthenticatorSetupScreen (post-MFA setup)
 * - RetryScreen (retry flows)
 * 
 * Usage:
 * const result = await PolicyEvaluationService.evaluateAndNavigate(dispatch, navigate, params);
 */
export class PolicyEvaluationService {
  private static recordPolicyResult(
    collector: (payload: {
      policyType?: string | null;
      isRequired?: boolean;
      reason?: string | null;
      details?: string | null;
    }) => void,
    result?: PolicyEvaluateResponse
  ) {
    if (!result) {
      return;
    }

    collector({
      policyType: result.policyType ?? null,
      isRequired: result.isRequired,
      reason: result.reason ?? null,
      details: result.details ?? null,
    });
  }
  /**
   * Main entry point for policy evaluation
   * Evaluates MFA first, then MAM for Android devices
   * Handles all navigation automatically
   */
  static async evaluateAndNavigate(
    dispatch: AppDispatch,
    navigate: NavigateFunction,
    params: PolicyEvaluationParams,
    options?: { skipMfa?: boolean; mfaResult?: PolicyEvaluateResponse }
  ): Promise<PolicyEvaluationResult> {
    // Declare mfaResult outside the try block so we can reference it in the catch (for failedStep determination)
    let mfaResult: PolicyEvaluateResponse | undefined = options?.mfaResult;
    // Track if this is the initial evaluation from auth redirect (not a retry)
    const isFirstScreenAfterAuth = !options?.skipMfa;
    try {

      if (mfaResult) {
        this.recordPolicyResult(collectMfaPolicyResult, mfaResult);
      }

      // Step 1: Evaluate MFA (unless skipped)
      if (!options?.skipMfa) {
        try {
          startPolicyEvaluationTiming('mfa');
          
          mfaResult = await this.evaluateMfa(dispatch, params);
          this.recordPolicyResult(collectMfaPolicyResult, mfaResult);

          if (mfaResult.isRequired) {
            collectAuthStatus('not-set', true);
            NavigationService.navigate(navigate, ROUTES.AUTH_SETUP, {
              selectedDevice: params.selectedDevice,
              policyParams: params,
              mfaResult,
              isFirstScreenAfterAuth,
            });
            return { mfaResult, navigated: true };
          }
          
          collectAuthStatus('set', false);
          endPolicyEvaluationTiming('mfa');
        } catch (mfaError) {
          errorDev('PolicyEvaluationService: MFA evaluation failed', mfaError);
          // Log initial MFA call failure (attempt 0)
          handleRetryFailure(PolicyEvaluationStep.MFA, 0, mfaError, undefined);
          return {
            mfaResult: {} as PolicyEvaluateResponse,
            navigated: false,
            error: mfaError,
            failedStep: PolicyEvaluationStep.MFA,
          };
        }
      }

      // Step 2: Evaluate MAM (for Android only)
      if (params.selectedDevice === Device.ANDROID) {
        startPolicyEvaluationTiming('mam');
        return await this.evaluateMamAndNavigate(dispatch, navigate, params, mfaResult, isFirstScreenAfterAuth);
      }
      
      // iOS: skip MAM, go to app download (mamResult not applicable for iOS)
      NavigationService.navigate(navigate, ROUTES.APP_DOWNLOAD, {
        selectedDevice: params.selectedDevice,
        mfaResult,
        mamResult: { isRequired: false, reason: 'ios-not-applicable' },
        isFirstScreenAfterAuth,
      });
      return { mfaResult: mfaResult || ({} as PolicyEvaluateResponse), navigated: true };
    } catch (error) {
      errorDev('PolicyEvaluationService: Evaluation failed', error);
      this.recordPolicyResult(collectMfaPolicyResult, undefined);
      this.recordPolicyResult(collectMamPolicyResult, undefined);
      // Record initial call failure (attempt 0) - determine which step failed
      const failedStep = mfaResult ? PolicyEvaluationStep.MAM : PolicyEvaluationStep.MFA;
      handleRetryFailure(failedStep, 0, error, undefined);
      return {
        mfaResult: {} as PolicyEvaluateResponse,
        navigated: false,
        error: error, 
        failedStep,
      };
    }
  }

  /**
   * Evaluate MFA policy only
   * Does NOT navigate - returns the result for caller to handle
   */
  static async evaluateMfa(
    dispatch: AppDispatch,
    params: PolicyEvaluationParams,
    isRetry: boolean = false
  ): Promise<PolicyEvaluateResponse> {
    // Check cache first
    const cachedResult = PolicyCacheService.getMfaCache(params);
        if (cachedResult) {
          debugLog('PolicyEvaluationService: Using cached MFA result');
          this.recordPolicyResult(collectMfaPolicyResult, cachedResult);
          return cachedResult;
        }

    try {
      // Mark step for timing the initial MFA API call (not for retries)
      if (!isRetry) {
        telemetryManager.markStep('mfa-call');
      }
      
      const mfaResult = await retryOnProvisioning(() =>
              dispatch(flwOrchestratorApi.endpoints.evaluateMfa.initiate({
          environment: params.env,
          payload: {
            userId: params.userId,
            applicationId: params.applicationId,
            devicePlatform: params.devicePlatform,
            clientAppType: params.clientAppType,
          },
          region: params.region,
        })
      ).unwrap());

      debugLog('PolicyEvaluationService: MFA Result:', mfaResult);
      this.recordPolicyResult(collectMfaPolicyResult, mfaResult);
      
      // Cache successful result
      PolicyCacheService.setMfaCache(params, mfaResult);
      
      return mfaResult;
    } catch (error) {
      errorDev('PolicyEvaluationService: MFA evaluation failed', error);
      this.recordPolicyResult(collectMfaPolicyResult, undefined);
      throw error;
    }
  }

  /**
   * Evaluate MAM policy and handle navigation
   * For Android: navigates to company-portal-install or app-download
   * For iOS: not typically called (MAM skipped for iOS)
   */
  static async evaluateMam(
    dispatch: AppDispatch,
    params: PolicyEvaluationParams,
    mfaResult?: PolicyEvaluateResponse,
    isRetry: boolean = false
  ): Promise<PolicyEvaluateResponse> {
    // Check cache first
    const cachedResult = PolicyCacheService.getMamCache(params);
    if (cachedResult) {
      debugLog('PolicyEvaluationService: Using cached MAM result');
      this.recordPolicyResult(collectMamPolicyResult, cachedResult);
      return cachedResult;
    }

    try {
      // Mark step for timing the initial MAM API call (not for retries)
      if (!isRetry) {
        telemetryManager.markStep('mam-call');
      }
      
      const mamResult = await retryOnProvisioning(() =>
               dispatch(flwOrchestratorApi.endpoints.evaluateMam.initiate({
          environment: params.env,
          payload: {
            userId: params.userId,
            applicationId: params.applicationId,
            devicePlatform: params.devicePlatform,
            clientAppType: params.clientAppType,
          },
          region: params.region,
        })
      ).unwrap());

      debugLog('PolicyEvaluationService: MAM Result:', mamResult);
      this.recordPolicyResult(collectMamPolicyResult, mamResult);
      
      // Cache successful result
      PolicyCacheService.setMamCache(params, mamResult);
      
      return mamResult;
    } catch (error) {
      errorDev('PolicyEvaluationService: MAM evaluation failed', error);
      this.recordPolicyResult(collectMamPolicyResult, undefined);
      throw error;
    }
  }

  /**
   * Evaluate MAM and handle navigation
   * Private method - use evaluateAndNavigate() instead
   */
  private static async evaluateMamAndNavigate(
    dispatch: AppDispatch,
    navigate: NavigateFunction,
    params: PolicyEvaluationParams,
    mfaResult?: PolicyEvaluateResponse,
    isFirstScreenAfterAuth?: boolean,
    isRetry: boolean = false
  ): Promise<PolicyEvaluationResult> {
    try {
      const mamResult = await this.evaluateMam(dispatch, params, mfaResult, isRetry);
      
      collectCompanyPortalStatus(mamResult.isRequired);
      const route = mamResult.isRequired ? ROUTES.COMPANY_PORTAL_INSTALL : ROUTES.APP_DOWNLOAD;
      
      NavigationService.navigate(navigate, route, {
        selectedDevice: params.selectedDevice,
        mfaResult,
        mamResult,
        isFirstScreenAfterAuth,
      }, { replace: true });

      return { mfaResult: mfaResult || ({} as PolicyEvaluateResponse), mamResult, navigated: true };
    } catch (error) {
      errorDev('PolicyEvaluationService: MAM evaluation and navigation failed', error);
      this.recordPolicyResult(collectMamPolicyResult, undefined);
      return {
        mfaResult: mfaResult || ({} as PolicyEvaluateResponse),
        navigated: false,
        error,
        failedStep: PolicyEvaluationStep.MAM,
      };
    }
  }

  /**
   * Handle retry of policy evaluation
   * Retries up to 3 times before allowing "Continue"
   * Used by RetryScreen
   * Note: retryCount is already incremented by the caller (RetryScreen)
   */
  static async handleRetry(
    dispatch: AppDispatch,
    navigate: NavigateFunction,
    params: PolicyEvaluationParams,
    step: PolicyEvaluationStep,
    retryCount: number,
    mfaResult?: PolicyEvaluateResponse
  ): Promise<boolean> {
    // Navigate to loading screen normally (without replace)
    // The loading screen will be removed when we navigate away from it using replace: true
    NavigationService.navigate(navigate, ROUTES.LOADING);

    const startTime = performance.now();
    
    try {
      if (step === PolicyEvaluationStep.MFA) {
        // Start timing MFA retry evaluation
        startPolicyEvaluationTiming('mfa');
        
        // Retry MFA evaluation (pass isRetry=true to skip marking mfa-call step)
        const newMfaResult = await this.evaluateMfa(dispatch, params, true);

        if (newMfaResult.isRequired) {
          // Still required - go to auth setup (replace loading screen)
          NavigationService.navigate(navigate, ROUTES.AUTH_SETUP, {
            selectedDevice: params.selectedDevice,
            policyParams: params,
            mfaResult: newMfaResult,
          }, { replace: true });
          return true;
        }

        endPolicyEvaluationTiming('mfa');

        if (params.selectedDevice === Device.ANDROID) {
          startPolicyEvaluationTiming('mam');
          const mamResult = await this.evaluateMamAndNavigate(dispatch, navigate, params, newMfaResult);
          if (!mamResult.navigated || mamResult.error) {
            const duration = Math.round(performance.now() - startTime);
            // Log as initial MAM call (attempt 0) since this is first MAM evaluation after MFA retry succeeded
            handleRetryFailure(PolicyEvaluationStep.MAM, 0, mamResult.error ?? new Error('MAM evaluation failed after MFA skip'), duration);
            this.navigateToRetry(navigate, PolicyEvaluationStep.MAM, params, newMfaResult, 0, true, mamResult.mamResult);
            return false;
          }
          return true;
        }
        
        // iOS - go to app download (mamResult not applicable for iOS)
        NavigationService.navigate(navigate, ROUTES.APP_DOWNLOAD, {
          selectedDevice: params.selectedDevice,
          mfaResult: newMfaResult,
          mamResult: { isRequired: false, reason: 'ios-not-applicable' },
        }, { replace: true });
        return true;
      }
      
      // Retry MAM evaluation (pass isRetry=true to skip marking mam-call step)
      startPolicyEvaluationTiming('mam');
      const mamResult = await this.evaluateMamAndNavigate(dispatch, navigate, params, mfaResult, false, true);
      if (!mamResult.navigated || mamResult.error) {
        const duration = Math.round(performance.now() - startTime);
        const failure = mamResult.error ?? new Error('MAM evaluation did not complete during retry');
        handleRetryFailure(step, retryCount, failure, duration);
        this.navigateToRetry(navigate, step, params, mfaResult, retryCount, true, mamResult.mamResult);
        return false;
      }
      return true;
    } catch (error) {
      const duration = Math.round(performance.now() - startTime);
      errorDev('PolicyEvaluationService: Retry failed', error);
      handleRetryFailure(step, retryCount, error, duration);
      
      const maxAttempts = getFeatureFlag('retryMaxAttempts', 3);
      const status = (error as any)?.status || (error as any)?.response?.status;
      const navRetryCount = status == 424 ? maxAttempts : retryCount;
      
      this.navigateToRetry(navigate, step, params, mfaResult, navRetryCount, true);
      return false;
    }
  }

  /**
   * Handle "Continue" button after 3 failed attempts
   * For MFA: Continue to MAM evaluation (Android) or app download (iOS)
   * For MAM: Continue to app download
   */
  static async handleContinueAfterFailures(
    dispatch: AppDispatch,
    navigate: NavigateFunction,
    params: PolicyEvaluationParams,
    step: PolicyEvaluationStep,
    mfaResult?: PolicyEvaluateResponse
  ): Promise<void> {
    const startTime = performance.now();
    
    try {
      if (step === PolicyEvaluationStep.MFA) {
        // After MFA failures, evaluate MAM for Android or go to app download for iOS
        if (params.selectedDevice === Device.ANDROID) {
          const mamResult = await this.evaluateMamAndNavigate(dispatch, navigate, params, mfaResult);
          
          // Check if MAM evaluation succeeded
          if (!mamResult.navigated || mamResult.error) {
            const duration = Math.round(performance.now() - startTime);
            errorDev('MAM evaluation failed after MFA failures:', mamResult.error);
            // Log as initial MAM call (attempt 0) since this is first MAM evaluation after MFA failures
            handleRetryFailure(PolicyEvaluationStep.MAM, 0, mamResult.error ?? new Error('MAM evaluation failed after MFA failures'), duration);
            this.navigateToRetry(navigate, PolicyEvaluationStep.MAM, params, mfaResult, 0, false, mamResult.mamResult);
          }
        } else {
          // iOS: after MFA failures, skip MAM
          NavigationService.navigate(navigate, ROUTES.APP_DOWNLOAD, {
            selectedDevice: params.selectedDevice,
            mfaResult,
            mamResult: { isRequired: false, reason: 'ios-not-applicable' },
          });
        }
      } else {
        // After MAM failures, go to app download — pass mfaResult only, mamResult not available
        NavigationService.navigate(navigate, ROUTES.APP_DOWNLOAD, {
          selectedDevice: params.selectedDevice,
          mfaResult,
        });
      }
    } catch (error) {
      const duration = Math.round(performance.now() - startTime);
      errorDev('PolicyEvaluationService: Continue after failures failed', error);
      // Don't log as retry failure since retries are exhausted - just track as fatal error
      trackError('api-failure', error);
      NavigationService.navigate(navigate, ROUTES.ERROR);
    }
  }

  /**
   * Navigate to retry screen
   * @param retryCount - Optional retry count to pass along (defaults to 0 for initial retry)
   * @param replaceLoading - Whether to replace the loading screen in history (defaults to false)
   */
  static navigateToRetry(
    navigate: NavigateFunction,
    step: PolicyEvaluationStep,
    params: PolicyEvaluationParams,
    mfaResult?: PolicyEvaluateResponse,
    retryCount: number = 0,
    replaceLoading: boolean = false,
    mamResult?: PolicyEvaluateResponse
  ): void {
    const retryState = {
      step,
      retryCount,
      env: params.env,
      userId: params.userId,
      applicationId: params.applicationId,
      devicePlatform: params.devicePlatform,
      clientAppType: params.clientAppType,
      region: params.region,
      selectedDevice: params.selectedDevice,
      ...(mfaResult && { mfaResult }),
      ...(mamResult && { mamResult }),
    };

    // Use replace: true to replace the loading screen in history when coming from retry flow
    // This prevents users from getting stuck on loading screen when going back
    NavigationService.navigate(navigate, ROUTES.RETRY, retryState, replaceLoading ? { replace: true } : undefined);
  }
}

/**
 * Export type for retry state
 */
export interface RetryState {
  step: PolicyEvaluationStep;
  retryCount: number;
  env: Environment;
  userId: string;
  applicationId: string;
  devicePlatform: DevicePlatform;
  clientAppType: 'browser' | 'publicClient' | 'mobileAppsAndDesktopClients';
  region: string;
  selectedDevice: Device;
  mfaResult?: PolicyEvaluateResponse;
  mamResult?: PolicyEvaluateResponse;
}
